import { cn } from "./cn";

/**
 * A native select, styled to the app.
 *
 * Native rather than a custom listbox, and deliberately: these sit inside a
 * report card's toolbar, they hold at most a dozen short options, and the
 * platform control already gets keyboard handling, type-ahead, screen-reader
 * semantics and sensible behaviour on a phone for free. A hand-built menu would
 * be a worse version of all of that.
 *
 * Here in `ui/` because the dashboard needs one on every card and every page
 * that wanted a select before this re-declared its own — four near-identical
 * copies of the same class list, which is three too many to keep in step.
 */
export function Select<T extends string>({
  value,
  onChange,
  options,
  label,
  size = "sm",
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  /** Announced to a screen reader. These rarely have a visible label. */
  label: string;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as T)}
      aria-label={label}
      title={label}
      className={cn(
        "rounded-md border border-line bg-surface text-content",
        /*
         * A native select sizes itself to its widest option and will happily
         * be wider than the screen -- "Time to first response" in a KPI card
         * was pushing a phone's whole dashboard 148px sideways. `min-w-0` lets
         * it shrink inside a flex or grid parent; `max-w-full` stops it
         * escaping one that has no opinion.
         */
        "min-w-0 max-w-full",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        size === "sm" ? "h-7 px-2 text-xs" : "h-9 px-2.5 text-sm",
        className,
      )}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
